import type { CityTerminal, DaySums } from './deliveryApi';

/**
 * Чистая логика «Сверки доставок»: привязка заказов из выписки к аптекам и сравнение с суммами аптек.
 * Ничего не знает ни про Excel, ни про сервер, поэтому проверяется отдельно от экрана.
 *
 * Как заказ привязывается к аптеке (первое сработавшее правило):
 *  1. Заказ найден в Archive Orders: терминал из «Склада передачи КД» (последняя часть после «_»,
 *     «1119003_PP1» -> «PP1») ищется в справочнике по ID терминала;
 *  2. Заказ найден в Archive Orders: точное совпадение адреса доставки с адресом из справочника;
 *  3. Заказа нет в Archive Orders (или файл не загружен): точное совпадение адреса из самой выписки.
 * Если заказ найден в Archive Orders, но ни одно правило не сработало, адрес выписки не пробуется:
 * он совпадает с адресом точки продаж, а не доставки, и дал бы ложную привязку.
 */

/** Названия колонок в Excel-файлах (заголовки должны совпадать символ в символ, кроме регистра и пробелов по краям) */
export const SALES_COLUMNS = {
  orderId: 'Номер заказа (ID/RRN)',
  address: 'Адрес точки продаж',
  terminalId: 'ID терминала',
  amount: 'Сумма операции (т)'
} as const;

export const ARCHIVE_COLUMNS = {
  orderId: '№ заказа',
  address: 'Адрес самовывоза/доставки',
  warehouse: 'Склад передачи КД'
} as const;

/** Расхождение меньше этой суммы (в тенге) считается погрешностью */
export const DIFF_TOLERANCE = 0.1;

/** Строка файла: значения по названиям колонок, всегда текст (как в Excel) */
export type SheetRow = Record<string, string>;

export interface SheetTable {
  headers: string[];
  rows: SheetRow[];
}

/** Справочник, подготовленный для быстрого поиска: по заказу без перебора всех терминалов */
export interface TerminalIndex {
  byTerminalId: Map<string, CityTerminal>;
  byAddress: Map<string, CityTerminal>;
}

export type MatchSource = 'warehouse' | 'address' | 'none';

export interface Resolution {
  terminal: CityTerminal | null;
  source: MatchSource;
}

export interface ArchiveEntry {
  address: string;
  warehouse: string;
}

export interface SalesSums {
  total: number;
  /** Сумма заказов, которые не удалось привязать ни к одной аптеке */
  unassigned: number;
  /** Ключ - логин аптеки (created_by) */
  byPharmacy: Map<string, number>;
}

/** Как распределились строки выписки; четыре группы в сумме дают processed */
export interface MatchStats {
  processed: number;
  /** Найден терминал по складу передачи КД */
  warehouse: number;
  /** Найден адрес доставки из Archive Orders */
  archiveAddress: number;
  /** Заказа нет в Archive Orders, аптека определена по адресу из выписки */
  salesAddress: number;
  unassigned: number;
  /** Информационно: сколько заказов выписки не найдено в Archive Orders (входят в salesAddress и unassigned) */
  notInArchive: number;
  /** Строки, где сумма не читается как число (в расчет идут как 0) */
  badAmounts: number;
}

export interface SalesSummary {
  sums: SalesSums;
  stats: MatchStats;
}

export interface PharmacyRow {
  login: string;
  cities: string[];
  addresses: string[];
  /** null - выписка не загружена / суммы доставки за день не получены */
  statement: number | null;
  delivery: number | null;
  /** Выписка минус доставка; только когда известны обе суммы */
  diff: number | null;
}

export interface ComparisonTotals {
  statement: number | null;
  delivery: number | null;
  diff: number | null;
  unassigned: number | null;
}

/** Сумма с точностью до копеек: убирает хвосты вроде 12345.600000000002 после сложения дробей */
export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function hasDiff(diff: number | null): boolean {
  return diff !== null && Math.abs(diff) > DIFF_TOLERANCE;
}

/** Адрес для сравнения: без регистра, без лишних пробелов (в том числе неразрывных) по краям и внутри */
export function normalizeAddress(value: unknown): string {
  return String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
}

function normalizeTerminalId(value: unknown): string {
  return String(value === null || value === undefined ? '' : value).trim().toLowerCase();
}

/** Код терминала из «Склада передачи КД»: «1119003_PP1» -> «PP1»; без «_» или с пустой частью - null */
export function extractTerminalId(warehouse: unknown): string | null {
  if (warehouse === null || warehouse === undefined) return null;
  const parts = String(warehouse).trim().split('_');
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1].trim();
  return last === '' ? null : last;
}

/** При дублях в справочнике действует первая запись (как и порядок списка на экране) */
export function buildTerminalIndex(terminals: CityTerminal[]): TerminalIndex {
  const byTerminalId = new Map<string, CityTerminal>();
  const byAddress = new Map<string, CityTerminal>();

  terminals.forEach((terminal) => {
    const idKey = normalizeTerminalId(terminal.terminal_id);
    if (idKey && !byTerminalId.has(idKey)) byTerminalId.set(idKey, terminal);

    const addressKey = normalizeAddress(terminal.address);
    if (addressKey && !byAddress.has(addressKey)) byAddress.set(addressKey, terminal);
  });

  return { byTerminalId, byAddress };
}

function resolveByAddress(address: string, index: TerminalIndex): CityTerminal | null {
  const key = normalizeAddress(address);
  return key ? index.byAddress.get(key) || null : null;
}

/** Терминал заказа из Archive Orders: сначала склад передачи КД, затем адрес доставки */
export function resolveArchiveEntry(entry: ArchiveEntry, index: TerminalIndex): Resolution {
  const terminalId = extractTerminalId(entry.warehouse);
  if (terminalId) {
    const byWarehouse = index.byTerminalId.get(normalizeTerminalId(terminalId));
    if (byWarehouse) return { terminal: byWarehouse, source: 'warehouse' };
  }

  const byAddress = resolveByAddress(entry.address, index);
  return byAddress ? { terminal: byAddress, source: 'address' } : { terminal: null, source: 'none' };
}

function cellText(row: SheetRow, column: string): string {
  const value = row[column];
  return value === undefined || value === null ? '' : String(value);
}

function orderKey(row: SheetRow, column: string): string {
  return cellText(row, column).trim();
}

/** Заказы Archive Orders по номеру; при повторе номера действует последняя строка файла */
export function buildArchiveIndex(table: SheetTable): Map<string, ArchiveEntry> {
  const map = new Map<string, ArchiveEntry>();
  table.rows.forEach((row) => {
    const key = orderKey(row, ARCHIVE_COLUMNS.orderId);
    if (!key) return;
    map.set(key, {
      address: cellText(row, ARCHIVE_COLUMNS.address),
      warehouse: cellText(row, ARCHIVE_COLUMNS.warehouse)
    });
  });
  return map;
}

/**
 * Сумма из ячейки выписки. Ячейки читаются как отформатированный текст, поэтому кроме «1234.5» встречаются
 * «1 234,5» и «1,234.50»: пробелы (в том числе неразрывные) убираются, при наличии точки запятая считается
 * разделителем разрядов, иначе - десятичным. Нечитаемое значение - NaN.
 */
export function parseAmount(value: string): number {
  const text = value.replace(/[\s  ]/g, '');
  if (text === '') return 0;
  const normalized = text.indexOf('.') >= 0 ? text.replace(/,/g, '') : text.replace(',', '.');
  const parsed = parseFloat(normalized);
  return isFinite(parsed) ? parsed : NaN;
}

/** Привязка одной строки выписки; общая для расчета сумм и для выгрузки, чтобы они не расходились */
function resolveSalesRow(
  row: SheetRow,
  archiveMap: Map<string, ArchiveEntry> | null,
  index: TerminalIndex
): { resolution: Resolution; inArchive: boolean } {
  const key = orderKey(row, SALES_COLUMNS.orderId);
  const entry = archiveMap && key ? archiveMap.get(key) : undefined;
  if (entry) return { resolution: resolveArchiveEntry(entry, index), inArchive: true };

  const terminal = resolveByAddress(cellText(row, SALES_COLUMNS.address), index);
  return { resolution: terminal ? { terminal, source: 'address' } : { terminal: null, source: 'none' }, inArchive: false };
}

/**
 * Суммы выписки по аптекам. Без Archive Orders все заказы привязываются по адресу выписки (предварительный
 * расчет сразу после загрузки файла), с ним - по правилам из шапки файла.
 */
export function summarizeSales(sales: SheetTable, archive: SheetTable | null, index: TerminalIndex): SalesSummary {
  const archiveMap = archive ? buildArchiveIndex(archive) : null;
  const byPharmacy = new Map<string, number>();
  const stats: MatchStats = {
    processed: 0, warehouse: 0, archiveAddress: 0, salesAddress: 0, unassigned: 0, notInArchive: 0, badAmounts: 0
  };
  let total = 0;
  let unassigned = 0;

  sales.rows.forEach((row) => {
    const text = cellText(row, SALES_COLUMNS.amount);
    let amount = parseAmount(text);
    if (isNaN(amount)) {
      stats.badAmounts += 1;
      amount = 0;
    }

    stats.processed += 1;
    total += amount;

    const { resolution, inArchive } = resolveSalesRow(row, archiveMap, index);
    if (archiveMap && !inArchive) stats.notInArchive += 1;

    if (resolution.terminal) {
      const login = resolution.terminal.created_by;
      byPharmacy.set(login, (byPharmacy.get(login) || 0) + amount);
      if (resolution.source === 'warehouse') stats.warehouse += 1;
      else if (inArchive) stats.archiveAddress += 1;
      else stats.salesAddress += 1;
    } else {
      unassigned += amount;
      stats.unassigned += 1;
    }
  });

  byPharmacy.forEach((value, login) => byPharmacy.set(login, roundMoney(value)));
  return { sums: { total: roundMoney(total), unassigned: roundMoney(unassigned), byPharmacy }, stats };
}

/**
 * Строки выписки для выгрузки: у заказов, найденных в Archive Orders, адрес заменяется адресом доставки,
 * а ID терминала - кодом терминала из справочника. Исходные строки не меняются.
 */
export function buildExportRows(sales: SheetTable, archive: SheetTable, index: TerminalIndex): SheetRow[] {
  const archiveMap = buildArchiveIndex(archive);

  return sales.rows.map((row) => {
    const key = orderKey(row, SALES_COLUMNS.orderId);
    const entry = key ? archiveMap.get(key) : undefined;
    if (!entry) return row;

    const { terminal } = resolveArchiveEntry(entry, index);
    const next: SheetRow = { ...row };
    // Пустой адрес доставки не стирает адрес выписки
    if (entry.address.trim() !== '') next[SALES_COLUMNS.address] = entry.address;
    if (terminal && terminal.terminal_code) next[SALES_COLUMNS.terminalId] = terminal.terminal_code;
    return next;
  });
}

/** Строки сравнения: по одной на аптеку (у аптеки может быть несколько терминалов), в порядке справочника */
export function buildComparison(
  terminals: CityTerminal[],
  sales: SalesSummary | null,
  day: DaySums | null
): { rows: PharmacyRow[]; totals: ComparisonTotals } {
  const rows: PharmacyRow[] = [];
  const byLogin = new Map<string, PharmacyRow>();

  terminals.forEach((terminal) => {
    let row = byLogin.get(terminal.created_by);
    if (!row) {
      const statement = sales ? sales.sums.byPharmacy.get(terminal.created_by) || 0 : null;
      const delivery = day ? day.byPharmacy.get(terminal.created_by) || 0 : null;
      row = {
        login: terminal.created_by,
        cities: [],
        addresses: [],
        statement,
        delivery,
        diff: statement !== null && delivery !== null ? roundMoney(statement - delivery) : null
      };
      byLogin.set(terminal.created_by, row);
      rows.push(row);
    }
    if (terminal.city_name && row.cities.indexOf(terminal.city_name) < 0) row.cities.push(terminal.city_name);
    if (terminal.address && row.addresses.indexOf(terminal.address) < 0) row.addresses.push(terminal.address);
  });

  const statementTotal = sales ? sales.sums.total : null;
  const deliveryTotal = day ? day.total : null;
  return {
    rows,
    totals: {
      statement: statementTotal,
      delivery: deliveryTotal,
      diff: statementTotal !== null && deliveryTotal !== null ? roundMoney(statementTotal - deliveryTotal) : null,
      unassigned: sales ? sales.sums.unassigned : null
    }
  };
}
